import React, {
  useState, forwardRef, useCallback, useEffect,
} from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './MyPlan.styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { advancedHostingActions } from '@/redux/modules';
import { useParams } from 'react-router-dom';
import useServerStatus from '@/hooks/useServerStatus';
import { useIsMobile } from '@/hooks/useIsMobile';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';

import FavoriteButton from '@/pages/Products/AdvancedHostingPage/AdvancedHostingPageContent/components/ServerList/components/FavoriteButton';
import ServerNickname from '@/pages/Products/AdvancedHostingPage/AdvancedHostingPageContent/components/ServerList/components/ServerNickname';
import ServerStatus from '@/pages/Products/AdvancedHostingPage/AdvancedHostingPageContent/components/ServerList/components/ServerStatus';

import Terminal from '@/pages/Products/VpsOciDetailPage/components/Terminal';

const MyPlan = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const {
    productName,
    domain,
    nicknameFromStore,
    isFavoriteFromStore,
    serverInfo,
    isRebooting,
    hasVpsData,
    packageId,
  } = useSelector(
    state => ({
      productName: state.vpsOci?.data?.productName,
      domain: state.vpsOci?.data?.domain,
      nicknameFromStore: state.vpsOci?.data?.nickName,
      isFavoriteFromStore: state.vpsOci?.data?.isFavorite,
      serverInfo: state.vpsOci?.data?.serverInfo,
      isRebooting: state.vpsOci?.isRebooting,
      hasVpsData: Boolean(state.vpsOci?.data),
      packageId: state.vpsOci?.data?.packageId,
    }),
    shallowEqual,
  );
  const [nickname, setNickname] = useState(nicknameFromStore ?? '');
  const [isFavorite, setIsFavorite] = useState(Boolean(isFavoriteFromStore));

  const { id: hostingId } = useParams();

  const {
    serverStatus,
    serverStatusLoading,
  } = useServerStatus({
    serverInfo,
    serverId: hostingId,
    serverPackageId: packageId,
  });

  // Provisório até termos o status específico de reboot/rebuild no backend. Enquanto isso, se estiver em reboot, mostramos como offline e desabilitamos o botão.
  const status = isRebooting ? VpsOciStatusEnum.OFFLINE : serverStatus;

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;

  useEffect(() => {
    const nextNickname = nicknameFromStore ?? '';
    setNickname(currentNickname => (currentNickname === nextNickname ? currentNickname : nextNickname));
  }, [nicknameFromStore]);

  useEffect(() => {
    const nextIsFavorite = Boolean(isFavoriteFromStore);
    setIsFavorite(currentIsFavorite => (currentIsFavorite === nextIsFavorite ? currentIsFavorite : nextIsFavorite));
  }, [isFavoriteFromStore]);

  const handleFavoriteUpdateSuccess = useCallback(() => {
    setIsFavorite(currentValue => !currentValue);
  }, []);

  const handleNicknameUpdate = useCallback((serverId, nextNickname) => {
    dispatch(advancedHostingActions.updateServer.request({
      serverId,
      nickName: nextNickname,
      onSuccess: () => setNickname(nextNickname),
    }));
  }, [dispatch]);

  if (!productName || !hostingId || !hasVpsData) return null;

  return (
    <>
      <ContentSection ref={ref} direction="row" align="center" justify="space-between" testId="vpsoci-myplan-section">
        <Styles.InfoWrapper>
          <Styles.PlanInfo>
            <Styles.ProductName data-testid="vpsoci-myplan-product-name">
              {productName}
              {isMobile && (
                <FavoriteButton
                  serverId={hostingId}
                  isFavorite={isFavorite}
                  locale={vpsOciLocale?.myPlan?.tooltip}
                  onSuccess={handleFavoriteUpdateSuccess}
                />
              )}
            </Styles.ProductName>

            <ServerStatus
              loading={serverStatusLoading}
              status={status}
              testId="vpsoci-myplan-status-tag"
            />

            {!isMobile && (
              <FavoriteButton
                serverId={hostingId}
                isFavorite={isFavorite}
                locale={vpsOciLocale?.myPlan?.tooltip}
                onSuccess={handleFavoriteUpdateSuccess}
              />
            )}
          </Styles.PlanInfo>
          <Styles.Domain>
            {domain}
          </Styles.Domain>
          <ServerNickname
            serverId={hostingId}
            nickname={nickname}
            placeholder={vpsOciLocale?.myPlan?.setServerNickname}
            onUpdateNickname={handleNicknameUpdate}
          />
        </Styles.InfoWrapper>

        <Terminal />

      </ContentSection>
    </>
  );
});

MyPlan.displayName = 'MyPlan';

export default MyPlan;
